import type React from "react";
import { useState } from "react";
import { Box, Tabs, Tab, Paper, Button, Typography } from "@mui/material";
import { Assessment, CourseFormData, Module } from "./types/course.types";
import AboutCourseTab from "./tabs/AboutCourse";
import SummaryTab from "./tabs/Summary";
import ModulesLessonsTab from "./tabs/ModuleLessons";
import AssessmentsTab from "./tabs/AssessmentsTab";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CreateCourseForm: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [modules, setModules] = useState<Module[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [formData, setFormData] = useState<CourseFormData>({
    title: "",
    slug: "",
    shortDescription: "",
    fullDescription: "",

    thumbnailUrl: "",
    thumbnailFile: null,

    previewVideoUrl: "",

    status: "draft",

    durationHours: 0,
    durationWeeks: 0,

    difficultyLevel: "beginner",

    courseType: "free",
    courseCategory: "video",

    pdfUrl: "",
    blogContent: "",
    videoUrls: "",
    pdfFile: null,
    learningObjectives: [],
    skills: [],
    requirements: [],
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFormChange = (newData: Partial<CourseFormData>) => {
    setFormData((prev) => ({ ...prev, ...newData }));
  };

  const handleSaveCourse = () => {
    console.log("Saving course:", formData);
    console.log("Modules:", modules);
    // Handle save logic here
  };

  const handleModulesChange = (updatedModules: Module[]) => {
    setModules(updatedModules);
  };

  const handleAssessmentsChange = (updatedAssessments: Assessment[]) => {
    setAssessments(updatedAssessments);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Create New Course
        </Typography>
        <Button
          variant="contained"
          onClick={handleSaveCourse}
          sx={{ color: "white" }}
        >
          Save Course
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ boxShadow: 0 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="course creation tabs"
        >
          <Tab label="About Course" id="tab-0" aria-controls="tabpanel-0" />
          <Tab label="Summary" id="tab-1" aria-controls="tabpanel-1" />
          <Tab
            label="Modules & Lessons"
            id="tab-2"
            aria-controls="tabpanel-2"
          />
          <Tab label="Assessments" id="tab-3" aria-controls="tabpanel-3" />
        </Tabs>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <AboutCourseTab formData={formData} onFormChange={handleFormChange} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <SummaryTab formData={formData} onFormChange={handleFormChange} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <ModulesLessonsTab
            modules={modules}
            onModulesChange={handleModulesChange}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <AssessmentsTab
            assessments={assessments}
            onAssessmentsChange={handleAssessmentsChange}
          />
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default CreateCourseForm;
